// Chat context provider
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import {
  ChatState,
  Conversation,
  ConversationWithMessages,
  Message,
  CreateConversationRequest,
  WebSocketMessage,
  APIError
} from '../types';
import { apiService } from '../services/api';
import { webSocketService } from '../services/websocket';
import { useAuth } from './AuthContext';

// Actions
type ChatAction =
  | { type: 'LOAD_CONVERSATIONS_START' }
  | { type: 'LOAD_CONVERSATIONS_SUCCESS'; payload: Conversation[] }
  | { type: 'LOAD_CONVERSATIONS_FAILURE'; payload: string }
  | { type: 'LOAD_CONVERSATION_START' }
  | { type: 'LOAD_CONVERSATION_SUCCESS'; payload: ConversationWithMessages }
  | { type: 'LOAD_CONVERSATION_FAILURE'; payload: string }
  | { type: 'CREATE_CONVERSATION_START' }
  | { type: 'CREATE_CONVERSATION_SUCCESS'; payload: Conversation }
  | { type: 'CREATE_CONVERSATION_FAILURE'; payload: string }
  | { type: 'UPDATE_CONVERSATION_SUCCESS'; payload: Conversation }
  | { type: 'DELETE_CONVERSATION_SUCCESS'; payload: string }
  | { type: 'CLEAR_ALL_CONVERSATIONS_SUCCESS' }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'UPDATE_STREAMING_MESSAGE'; payload: { message: Message; conversationId: string; delta: string } }
  | { type: 'COMPLETE_STREAMING_MESSAGE'; payload: { message: Message; conversationId: string } }
  | { type: 'SET_TYPING'; payload: boolean }
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_CURRENT_CONVERSATION' }
  | { type: 'SET_CURRENT_CONVERSATION'; payload: Conversation }
  | { type: 'SET_PROGRESS'; payload: { status: string | null; message: string | null; conversationId: string | null } }
  | { type: 'CLEAR_PROGRESS' }
  | { type: 'UPDATE_MESSAGE_ATTACHMENTS'; payload: { messageId: string; attachmentFilenames: string[] } }
  | { type: 'UPDATE_MESSAGE_IMAGE_FILES'; payload: { messageId: string; imageFileIds: string[] } }
  | { type: 'UPDATE_MESSAGE_CSV_FILE'; payload: { messageId: string; fileId: string; filename: string; status: 'creating' | 'ready' } };

// Initial state
const initialState: ChatState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  isLoading: false,
  isConnected: false,
  isTyping: false,
  error: null,
  progress: {
    status: null,
    message: null,
    conversationId: null,
  },
};

// Reducer
const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'LOAD_CONVERSATIONS_START':
    case 'LOAD_CONVERSATION_START':
    case 'CREATE_CONVERSATION_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'LOAD_CONVERSATIONS_SUCCESS':
      return {
        ...state,
        conversations: action.payload,
        isLoading: false,
        error: null,
      };

    case 'LOAD_CONVERSATION_SUCCESS':
      return {
        ...state,
        currentConversation: action.payload,
        messages: action.payload.messages,
        isLoading: false,
        error: null,
      };

    case 'CREATE_CONVERSATION_SUCCESS':
      // CRITICAL: Set currentConversation immediately to avoid race condition.
      // Without this, sendMessage fails with "No conversation selected" if user types
      // before loadConversation completes when navigating to /chat/{newId}
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
        currentConversation: { ...action.payload, messages: [] },
        messages: [],
        isLoading: false,
        error: null,
      };

    case 'UPDATE_CONVERSATION_SUCCESS':
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv.id === action.payload.id ? action.payload : conv
        ),
        currentConversation: state.currentConversation?.id === action.payload.id 
          ? { ...state.currentConversation, ...action.payload }
          : state.currentConversation,
        error: null,
      };

    case 'DELETE_CONVERSATION_SUCCESS':
      return {
        ...state,
        conversations: state.conversations.filter(conv => conv.id !== action.payload),
        currentConversation: state.currentConversation?.id === action.payload ? null : state.currentConversation,
        messages: state.currentConversation?.id === action.payload ? [] : state.messages,
        error: null,
      };

    case 'CLEAR_ALL_CONVERSATIONS_SUCCESS':
      return {
        ...state,
        conversations: [],
        currentConversation: null,
        messages: [],
        error: null,
      };

    case 'ADD_MESSAGE':
      const newMessage = action.payload;
      let updatedMessages = [...(state.messages || [])];
      
      // Avoid duplicate messages
      const exists = updatedMessages.find(msg => msg.id === newMessage.id);
      if (!exists) {
        updatedMessages.push(newMessage);
        // Sort by created_at to maintain order
        updatedMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }

      return {
        ...state,
        messages: updatedMessages,
        currentConversation: state.currentConversation ? {
          ...state.currentConversation,
          messages: updatedMessages,
          updated_at: new Date().toISOString(),
        } : null,
      };

    case 'UPDATE_STREAMING_MESSAGE':
      // Update streaming message content
      const streamingMessages = [...(state.messages || [])];
      // Try to find by id or openai_message_id (in case id is OpenAI message ID)
      const streamingMessageIndex = streamingMessages.findIndex(msg => 
        msg.id === action.payload.message.id || 
        msg.openai_message_id === action.payload.message.id ||
        (action.payload.message.id?.startsWith('msg_') && msg.openai_message_id === action.payload.message.id)
      );
      
      // Mask CSV and image content during streaming to prevent it from being displayed
      // Completely remove [CSV_CONTENT_START]...content...[CSV_CONTENT_END] and [IMAGE_CONTENT_START]...content...[IMAGE_CONTENT_END] sections
      // Also mask the "📁 Fichier CSV disponible" line until message is complete
      const maskCsvContent = (content: string): string => {
        let masked = content;
        
        // Remove the entire CSV section including markers
        const csvStartIndex = masked.indexOf('[CSV_CONTENT_START]');
        if (csvStartIndex !== -1) {
          const csvEndIndex = masked.indexOf('[CSV_CONTENT_END]', csvStartIndex);
          if (csvEndIndex !== -1) {
            // Both markers found, remove everything from start to end (inclusive)
            const before = masked.substring(0, csvStartIndex);
            const after = masked.substring(csvEndIndex + '[CSV_CONTENT_END]'.length);
            masked = (before.trimEnd() + '\n\n' + after.trimStart()).trim();
          } else {
            // Start marker found but end marker not yet received, remove everything after start
            masked = masked.substring(0, csvStartIndex).trimEnd();
          }
        }
        
        // Remove the entire image section including markers (base64 encoded images)
        const imageStartIndex = masked.indexOf('[IMAGE_CONTENT_START]');
        if (imageStartIndex !== -1) {
          const imageEndIndex = masked.indexOf('[IMAGE_CONTENT_END]', imageStartIndex);
          if (imageEndIndex !== -1) {
            // Both markers found, remove everything from start to end (inclusive)
            const before = masked.substring(0, imageStartIndex);
            const after = masked.substring(imageEndIndex + '[IMAGE_CONTENT_END]'.length);
            masked = (before.trimEnd() + '\n\n' + after.trimStart()).trim();
          } else {
            // Start marker found but end marker not yet received, remove everything after start
            masked = masked.substring(0, imageStartIndex).trimEnd();
          }
        }
        
        // Note: We no longer mask attachment: references during streaming
        // Let them render - the MarkdownMessage component will handle them via sandbox-image endpoint
        
        // Mask the "📁 Fichier CSV disponible" line during streaming
        // This line will only appear once the message is complete with file_ids
        masked = masked.replace(/📁\s*Fichier\s+CSV\s+disponible[^\n]*/gi, '');
        
        // Clean up any extra newlines
        masked = masked.replace(/\n{3,}/g, '\n\n').trim();
        
        return masked;
      };
      
      const maskedContent = maskCsvContent(action.payload.message.content);
      
      if (streamingMessageIndex >= 0) {
        // Update existing streaming message
        streamingMessages[streamingMessageIndex] = {
          ...streamingMessages[streamingMessageIndex],
          content: maskedContent,
        };
      } else {
        // Add new streaming message
        // Ensure openai_message_id is set if id starts with 'msg_' (OpenAI message ID)
        const streamingMessage = {
          ...action.payload.message,
          content: maskedContent,
        };
        // If id is an OpenAI message ID (starts with 'msg_') and openai_message_id is not set, set it
        if (streamingMessage.id?.startsWith('msg_') && !streamingMessage.openai_message_id) {
          streamingMessage.openai_message_id = streamingMessage.id;
        }
        console.log('📝 UPDATE_STREAMING_MESSAGE: Adding new message to state.messages', {
          messageId: streamingMessage.id,
          openai_message_id: streamingMessage.openai_message_id,
          currentMessagesCount: streamingMessages.length
        });
        streamingMessages.push(streamingMessage);
        streamingMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }
      
      // After updating/adding streaming message, check if we need to apply any pending attachment_references
      // This will be handled by re-dispatching UPDATE_MESSAGE_ATTACHMENTS after messages are updated
      // For now, the reducer will handle it when the message exists

      return {
        ...state,
        messages: streamingMessages,
        currentConversation: state.currentConversation ? {
          ...state.currentConversation,
          messages: streamingMessages,
          updated_at: new Date().toISOString(),
        } : null,
      };

    case 'COMPLETE_STREAMING_MESSAGE':
      // Finalize streaming message - PREVENT DUPLICATES
      const completedMessages = [...(state.messages || [])];
      
      const msgId = action.payload.message.id;
      const msgOpenaiId = action.payload.message.openai_message_id;
      const msgContent = action.payload.message.content || '';
      
      // STRICT: Check if message already exists by ID or content BEFORE processing
      // This prevents duplicates from being added
      const existingMessage = completedMessages.find(msg => {
        // Match by exact id
        if (msg.id === msgId || msg.openai_message_id === msgId) {
          return true;
        }
        
        // Match by openai_message_id
        if (msgOpenaiId && (msg.openai_message_id === msgOpenaiId || msg.id === msgOpenaiId)) {
          return true;
        }
        
        // Match if message.id is OpenAI ID (starts with 'msg_') and matches openai_message_id
        if (msgId?.startsWith('msg_') && msg.openai_message_id === msgId) {
          return true;
        }
        
        // Match by exact content (for assistant messages) - strict check to prevent duplicates
        if (msg.role === 'assistant' && action.payload.message.role === 'assistant' && msgContent.length > 50) {
          if (msg.content === msgContent) {
            return true;
          }
        }
        
        return false;
      });
      
      // Sanitize metadata.image_file_ids: filter out empty strings
      const payloadMsg = action.payload.message;
      const sanitizedPayload: Message = payloadMsg.metadata?.image_file_ids
        ? { ...payloadMsg, metadata: { ...payloadMsg.metadata, image_file_ids: payloadMsg.metadata.image_file_ids.filter((id: string) => id && String(id).trim()) } }
        : payloadMsg;

      // If message already exists, UPDATE it instead of adding duplicate
      if (existingMessage) {
        const existingIndex = completedMessages.indexOf(existingMessage);
        const finalMessage = {
          ...existingMessage,
          ...sanitizedPayload,
          // Preserve existing id and openai_message_id
          id: existingMessage.id,
          openai_message_id: action.payload.message.id?.startsWith('msg_') 
            ? action.payload.message.id 
            : (existingMessage.openai_message_id || action.payload.message.openai_message_id),
        };
        completedMessages[existingIndex] = finalMessage;
        console.log('✅ COMPLETE_STREAMING_MESSAGE: Updated existing message (prevented duplicate)');
      } else {
        // Message doesn't exist, add it
        const finalMessage = {
          ...sanitizedPayload,
          // Ensure openai_message_id is set if message.id is an OpenAI ID
          openai_message_id: action.payload.message.id?.startsWith('msg_') 
            ? action.payload.message.id 
            : action.payload.message.openai_message_id,
        };
        completedMessages.push(finalMessage);
        completedMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        console.log('✅ COMPLETE_STREAMING_MESSAGE: Added new message');
      }

      return {
        ...state,
        messages: completedMessages,
        currentConversation: state.currentConversation ? {
          ...state.currentConversation,
          messages: completedMessages,
          updated_at: new Date().toISOString(),
        } : null,
      };

    case 'LOAD_CONVERSATIONS_FAILURE':
    case 'LOAD_CONVERSATION_FAILURE':
    case 'CREATE_CONVERSATION_FAILURE':
    case 'SET_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case 'SET_TYPING':
      return {
        ...state,
        isTyping: action.payload,
      };

    case 'SET_CONNECTED':
      return {
        ...state,
        isConnected: action.payload,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'CLEAR_CURRENT_CONVERSATION':
      return {
        ...state,
        currentConversation: null,
        messages: [],
      };

    case 'SET_CURRENT_CONVERSATION':
      // Convert Conversation to ConversationWithMessages
      const conversationWithMessages = {
        ...action.payload,
        messages: (action.payload as any).messages || []
      } as ConversationWithMessages;
      
      return {
        ...state,
        currentConversation: conversationWithMessages,
        messages: conversationWithMessages.messages,
      };

    case 'SET_PROGRESS':
      return {
        ...state,
        progress: action.payload,
      };

    case 'CLEAR_PROGRESS':
      return {
        ...state,
        progress: {
          status: null,
          message: null,
          conversationId: null,
        },
      };

    case 'UPDATE_MESSAGE_ATTACHMENTS':
      // Update message with attachment filenames for sandbox-image endpoint
      // messageId can be either DB id or OpenAI message_id, so check both
      // Check both state.messages and currentConversation.messages
      const allMessagesForUpdate = [
        ...state.messages,
        ...(state.currentConversation?.messages || [])
      ];
      const uniqueMessagesForUpdate = Array.from(
        new Map(allMessagesForUpdate.map(m => [m.id || m.openai_message_id, m])).values()
      );
      
      let foundMessage = false;
      console.log('📎 UPDATE_MESSAGE_ATTACHMENTS reducer:', {
        messageId: action.payload.messageId,
        stateMessagesCount: state.messages.length,
        conversationMessagesCount: state.currentConversation?.messages?.length || 0,
        allMessagesCount: uniqueMessagesForUpdate.length,
        messageIds: uniqueMessagesForUpdate.map(m => ({ id: m.id, openai_id: m.openai_message_id }))
      });
      
      const updatedMessagesWithAttachments = uniqueMessagesForUpdate.map(msg => {
        // Match by id (DB) or openai_message_id (OpenAI)
        if (msg.id === action.payload.messageId || msg.openai_message_id === action.payload.messageId) {
          foundMessage = true;
          console.log('✅ UPDATE_MESSAGE_ATTACHMENTS: Found message, updating with attachments:', {
            messageId: msg.id,
            openai_message_id: msg.openai_message_id,
            attachmentFilenames: action.payload.attachmentFilenames
          });
          return {
            ...msg,
            metadata: {
              ...msg.metadata,
              attachment_filenames: action.payload.attachmentFilenames,
            },
          };
        }
        return msg;
      });
      
      // If message not found, it might be created later - the reducer will handle it when UPDATE_STREAMING_MESSAGE or ADD_MESSAGE is called
      // For now, just return the state (the attachment_references will be applied when the message is created)
      if (!foundMessage) {
        console.log('⚠️ UPDATE_MESSAGE_ATTACHMENTS: Message not found yet, will be applied when message is created:', action.payload.messageId);
        // Store in a pending attachments map that will be checked when messages are added
        // For now, return state as-is - the attachment will be applied when the message arrives
        return state;
      }
      
      return {
        ...state,
        messages: updatedMessagesWithAttachments,
        currentConversation: state.currentConversation ? {
          ...state.currentConversation,
          messages: updatedMessagesWithAttachments,
        } : null,
      };

    case 'UPDATE_MESSAGE_IMAGE_FILES':
      // Update message with image file IDs
      // messageId can be either DB id or OpenAI message_id, so check both
      let foundMessageInState = false;
      const updatedMessagesWithImages = state.messages.map(msg => {
        // Match by id (DB) or openai_message_id (OpenAI)
        if (msg.id === action.payload.messageId || msg.openai_message_id === action.payload.messageId) {
          foundMessageInState = true;
          return {
            ...msg,
            metadata: {
              ...msg.metadata,
              image_file_ids: action.payload.imageFileIds,
            },
          };
        }
        return msg;
      });
      
      let updatedConversationMessages = state.currentConversation?.messages || [];
      let foundMessageInConversation = false;
      if (state.currentConversation) {
        updatedConversationMessages = state.currentConversation.messages.map(msg => {
          if (msg.id === action.payload.messageId || msg.openai_message_id === action.payload.messageId) {
            foundMessageInConversation = true;
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                image_file_ids: action.payload.imageFileIds,
              },
            };
          }
          return msg;
        });
      }
      
      if (!foundMessageInState && !foundMessageInConversation) {
        // Only log if message_id is not 'temp' (which indicates a real message that should exist)
        if (action.payload.messageId && action.payload.messageId !== 'temp') {
          console.log('⚠️ UPDATE_MESSAGE_IMAGE_FILES: Message not found, will be applied later:', action.payload.messageId);
        }
        return state;
      }
      
      return {
        ...state,
        messages: updatedMessagesWithImages,
        currentConversation: state.currentConversation ? {
          ...state.currentConversation,
          messages: updatedConversationMessages,
          updated_at: new Date().toISOString(),
        } : state.currentConversation,
      };

    case 'UPDATE_MESSAGE_CSV_FILE':
      // Update message with CSV file information
      // messageId can be either DB id or OpenAI message_id, so check both
      let foundCsvMessageInState = false;
      const updatedMessagesWithCsv = state.messages.map(msg => {
        // Match by id (DB) or openai_message_id (OpenAI)
        if (msg.id === action.payload.messageId || msg.openai_message_id === action.payload.messageId) {
          foundCsvMessageInState = true;
          return {
            ...msg,
            metadata: {
              ...msg.metadata,
              csv_file_id: action.payload.fileId,
              csv_filename: action.payload.filename,
              csv_status: action.payload.status,
              // Also update file_ids if not already present
              file_ids: msg.metadata?.file_ids || [],
            },
          };
        }
        return msg;
      });
      
      let updatedConversationMessagesCsv = state.currentConversation?.messages || [];
      let foundCsvMessageInConversation = false;
      if (state.currentConversation) {
        updatedConversationMessagesCsv = state.currentConversation.messages.map(msg => {
          if (msg.id === action.payload.messageId || msg.openai_message_id === action.payload.messageId) {
            foundCsvMessageInConversation = true;
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                csv_file_id: action.payload.fileId,
                csv_filename: action.payload.filename,
                csv_status: action.payload.status,
                // Also update file_ids if not already present
                file_ids: msg.metadata?.file_ids || [],
              },
            };
          }
          return msg;
        });
      }
      
      if (!foundCsvMessageInState && !foundCsvMessageInConversation) {
        console.log('⚠️ UPDATE_MESSAGE_CSV_FILE: Message not found in state.messages or currentConversation.messages, will be applied by useEffect later:', action.payload.messageId);
        return state;
      }
      
      return {
        ...state,
        messages: updatedMessagesWithCsv,
        currentConversation: state.currentConversation ? {
          ...state.currentConversation,
          messages: updatedConversationMessagesCsv,
          updated_at: new Date().toISOString(),
        } : state.currentConversation,
      };

    default:
      return state;
  }
};

// Context type
interface ChatContextType extends ChatState {
  loadConversations: () => Promise<void>;
  loadConversation: (id: string) => Promise<void>;
  createConversation: (data: CreateConversationRequest) => Promise<Conversation>;
  updateConversation: (id: string, title: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  clearAllConversations: () => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  clearError: () => void;
  clearCurrentConversation: () => void;
}

// Create context
const ChatContext = createContext<ChatContextType | undefined>(undefined);

// Provider props
interface ChatProviderProps {
  children: ReactNode;
}

// Chat provider component
export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { user, isAuthenticated } = useAuth();
  
  // Store pending attachment_references that arrive before messages are created
  const pendingAttachmentsRef = React.useRef<Map<string, string[]>>(new Map());
  // Store pending image_file_ids that arrive before messages are created
  const pendingImageFilesRef = React.useRef<Map<string, string[]>>(new Map());

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    // Reduced logging - only log important message types
    if (['error', 'file', 'csv_file_created', 'csv_file_ready', 'image_file'].includes(message.type)) {
      console.log('📨 WebSocket message:', message.type);
    }

    switch (message.type) {
      case 'message':
        if (message.message) {
          console.log('📝 Regular message received:', message.message);
          console.log('📝 Message metadata:', message.message.metadata);
          if (message.message.metadata?.file_ids) {
            console.log('📝 Message has file_ids:', message.message.metadata.file_ids);
          }
          dispatch({ type: 'ADD_MESSAGE', payload: message.message });
        }
        break;
      
      case 'stream':
        // Handle streaming text updates
        if (message.message && message.delta && message.conversation_id) {
          // Reduced logging - only log first few updates or large deltas
          // (removed verbose logging to reduce console spam)
          
          dispatch({
            type: 'UPDATE_STREAMING_MESSAGE',
            payload: {
              message: message.message,
              conversationId: message.conversation_id,
              delta: message.delta
            }
          });
        }
        break;
      
      case 'message_complete':
        // Handle completion of streaming message
        if (message.message && message.conversation_id) {
          console.log('✅ Stream complete for message:', message.message.id);
          console.log('📝 message_complete metadata:', message.message.metadata);
          console.log('📝 message_complete file_ids:', message.message.metadata?.file_ids);
          
          dispatch({
            type: 'COMPLETE_STREAMING_MESSAGE', 
            payload: {
              message: message.message,
              conversationId: message.conversation_id
            }
          });
          
          // Clear any ongoing progress status when message is complete
          // This prevents "thinking" or other statuses from persisting after completion
          dispatch({ 
            type: 'SET_PROGRESS', 
            payload: { 
              status: null, 
              message: null,
              conversationId: message.conversation_id
            } 
          });
        }
        break;

      case 'typing':
        if (typeof message.is_typing === 'boolean') {
          dispatch({ type: 'SET_TYPING', payload: message.is_typing });
        }
        break;

      case 'progress':
        // Only log important status changes (not duplicates)
        // Reduced logging to avoid console spam
        if (message.status && ['completed', 'failed', 'tool_running', 'retrieving_csv', 'retrieving_images'].includes(message.status)) {
          console.log(`📊 Progress: ${message.status}`, message.message || '');
        }
        
        // Dispatch immediately - no delays
        dispatch({ 
          type: 'SET_PROGRESS', 
          payload: { 
            status: message.status || null, 
            message: typeof message.message === 'string' ? message.message : null,
            conversationId: message.conversation_id || null
          } 
        });
        
        // Clear progress when completed, but keep it visible for at least 2 seconds
        if (message.status === 'completed') {
          setTimeout(() => {
            dispatch({ type: 'CLEAR_PROGRESS' });
          }, 2000); // Increased from 1000ms to 2000ms for better visibility
        }
        break;

            case 'file':
                console.log('📁 File message received:', message);
                console.log('📁 File ID:', message.file_id);
                console.log('📁 Download URL:', message.download_url);
                console.log('📁 Message data:', message.message);
                if (message.message) {
                    // Add file message with download URL
                    const fileMessage = {
                        ...message.message,
                        metadata: {
                            ...message.message.metadata,
                            file_id: message.file_id,
                            download_url: message.download_url
                        }
                    };
                    console.log('📁 Final file message to add:', fileMessage);
                    dispatch({ type: 'ADD_MESSAGE', payload: fileMessage });
                }
                break;

      case 'conversation_switched':
        if (message.conversation) {
          console.log('🔄 Backend switched conversation:', message.conversation.id);
          dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: message.conversation });
          // Also update the URL to reflect the actual conversation
          if (window.location.pathname.includes('/dashboard/chat/')) {
            const newPath = `/dashboard/chat/${message.conversation.id}`;
            if (window.location.pathname !== newPath) {
              window.history.pushState({}, '', newPath);
            }
          }
        }
        break;

      case 'error':
        const errorMsg = typeof message.message === 'string' ? message.message : 'WebSocket error';
        dispatch({ type: 'SET_ERROR', payload: errorMsg });
        dispatch({ type: 'CLEAR_PROGRESS' });
        break;

      case 'connection':
        console.log('✅ WebSocket connection confirmed');
        break;

      case 'pong':
        // Handle pong messages (connection health)
        break;

      case 'attachment_references':
        // Handle attachment references for images (sandbox-image endpoint)
        if (message.message_id && message.attachment_filenames) {
          console.log('📎 Attachment references received:', {
            messageId: message.message_id,
            filenames: message.attachment_filenames,
          });
          
          // Always store in pending attachments ref first
          pendingAttachmentsRef.current.set(message.message_id, message.attachment_filenames);
          
          // Always dispatch UPDATE_MESSAGE_ATTACHMENTS - the reducer will handle matching
          // This ensures it works even if the message was just created in COMPLETE_STREAMING_MESSAGE
          dispatch({
            type: 'UPDATE_MESSAGE_ATTACHMENTS',
            payload: {
              messageId: message.message_id,
              attachmentFilenames: message.attachment_filenames,
            },
          });
        }
        break;

      case 'image_update':
        // Handle image file IDs update (filter out empty strings)
        const validImageIds = (message.image_file_ids || []).filter((id: string) => id && String(id).trim());
        if (message.message_id && validImageIds.length > 0) {
          console.log('🖼️ Image update received:', {
            messageId: message.message_id,
            imageFileIds: validImageIds
          });
          dispatch({
            type: 'UPDATE_MESSAGE_IMAGE_FILES',
            payload: {
              messageId: message.message_id,
              imageFileIds: validImageIds,
            },
          });
        }
        break;

      case 'image_file':
        // Handle individual image file (for backward compatibility)
        if (message.message_id && message.file_id) {
          // Find the message and add file_id to image_file_ids
          // Check both id (DB) and openai_message_id (OpenAI)
          // Also check currentConversation.messages
          const allMessages = [
            ...state.messages,
            ...(state.currentConversation?.messages || [])
          ];
          const uniqueMessages = Array.from(
            new Map(allMessages.map(m => [m.id || m.openai_message_id, m])).values()
          );
          
          const messageToUpdate = uniqueMessages.find(msg => 
            msg.id === message.message_id || msg.openai_message_id === message.message_id
          );
          
          if (messageToUpdate) {
            const existingImageFileIds = messageToUpdate.metadata?.image_file_ids || [];
            if (!existingImageFileIds.includes(message.file_id)) {
              dispatch({
                type: 'UPDATE_MESSAGE_IMAGE_FILES',
                payload: {
                  messageId: message.message_id,
                  imageFileIds: [...existingImageFileIds, message.file_id],
                },
              });
            }
          } else {
            // Message not found yet - store in pending for later processing
            const existingPending = pendingImageFilesRef.current.get(message.message_id) || [];
            if (!existingPending.includes(message.file_id)) {
              pendingImageFilesRef.current.set(message.message_id, [...existingPending, message.file_id]);
            }
          }
        }
        break;

      case 'csv_file_created':
        // Handle CSV file creation event (file is being created progressively)
        if (message.message_id && message.file_id && message.filename) {
          console.log('📊 CSV file created event received:', {
            messageId: message.message_id,
            fileId: message.file_id,
            filename: message.filename,
            status: message.status
          });
          dispatch({
            type: 'UPDATE_MESSAGE_CSV_FILE',
            payload: {
              messageId: message.message_id,
              fileId: message.file_id,
              filename: message.filename,
              status: (message.status as 'creating' | 'ready') || 'creating',
            },
          });
        }
        break;

      case 'csv_file_ready':
        // Handle CSV file ready event (file is finalized and ready for download)
        if (message.message_id && message.file_id && message.filename) {
          console.log('✅ CSV file ready event received:', {
            messageId: message.message_id,
            fileId: message.file_id,
            filename: message.filename,
            status: message.status
          });
          dispatch({
            type: 'UPDATE_MESSAGE_CSV_FILE',
            payload: {
              messageId: message.message_id,
              fileId: message.file_id,
              filename: message.filename,
              status: 'ready',
            },
          });
        }
        break;

      case 'image_file_ready':
        // Handle individual image file ready event
        if (message.file && message.message_id) {
          const { file_id } = message.file;
          // Use UPDATE_MESSAGE_IMAGE_FILES which already exists in the reducer
          dispatch({
            type: 'UPDATE_MESSAGE_IMAGE_FILES',
            payload: {
              messageId: message.message_id,
              imageFileIds: [file_id]
            }
          });
        }
        break;

      case 'system':
        // System messages are informational and can be silently ignored
        break;

      default:
        // Only log unknown types that aren't expected to be handled
        if (!['ping', 'pong', 'connection', 'system'].includes(message.type)) {
          console.log('⚠️ Unknown WebSocket message type:', message.type);
        }
    }
  }, [state.messages]);

  // Connection status handler
  const handleConnectionStatus = useCallback((connected: boolean) => {
    console.log(`🔌 WebSocket connection status: ${connected ? 'connected' : 'disconnected'}`);
    dispatch({ type: 'SET_CONNECTED', payload: connected });
  }, []);

  // Setup WebSocket when user is authenticated
  useEffect(() => {
    let isComponentMounted = true;
    let connectionTimeout: NodeJS.Timeout | null = null;

    if (isAuthenticated && user) {
      console.log('🔌 Setting up WebSocket connection for user:', user.id);

      // Setup event listeners
      const removeMessageHandler = webSocketService.onMessage(handleWebSocketMessage);
      const removeConnectionHandler = webSocketService.onConnection(handleConnectionStatus);
      
      // Delay connection slightly to handle React StrictMode double-mounting
      connectionTimeout = setTimeout(() => {
        if (isComponentMounted) {
          webSocketService.connect(user.id).catch(error => {
            if (isComponentMounted) {
              console.error('❌ Failed to connect to WebSocket:', error);
              dispatch({ type: 'SET_ERROR', payload: 'Failed to establish real-time connection' });
            }
          });
        }
      }, 100);

      // Cleanup function
      return () => {
        isComponentMounted = false;
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
        }
        removeMessageHandler();
        removeConnectionHandler();
        // Only disconnect if this is a real unmount, not React StrictMode
        setTimeout(() => {
          if (!isComponentMounted) {
            webSocketService.disconnect();
          }
        }, 50);
      };
    } else {
      // Disconnect when user is not authenticated
      webSocketService.disconnect();
      dispatch({ type: 'SET_CONNECTED', payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]); // Only reconnect when auth status or user ID changes

  // Load conversations
  const loadConversations = useCallback(async (): Promise<void> => {
    try {
      dispatch({ type: 'LOAD_CONVERSATIONS_START' });
      const conversations = await apiService.getConversations();
      dispatch({ type: 'LOAD_CONVERSATIONS_SUCCESS', payload: conversations });
      console.log('✅ Conversations loaded:', conversations.length);
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to load conversations';
      console.error('❌ Failed to load conversations:', errorMessage);
      dispatch({ type: 'LOAD_CONVERSATIONS_FAILURE', payload: errorMessage });
    }
  }, []);

  // Load specific conversation
  const loadConversation = useCallback(async (id: string): Promise<void> => {
    try {
      dispatch({ type: 'LOAD_CONVERSATION_START' });
      const conversation = await apiService.getConversation(id);
      
      // Debug: Check for CSV metadata in loaded messages
      const messagesWithCsv = conversation.messages?.filter(msg => 
        msg.metadata?.csv_file_id || msg.metadata?.file_ids?.length > 0
      ) || [];
      if (messagesWithCsv.length > 0) {
        console.log(`📊 Found ${messagesWithCsv.length} messages with CSV metadata:`, 
          messagesWithCsv.map(msg => ({
            id: msg.id,
            csv_file_id: msg.metadata?.csv_file_id,
            csv_filename: msg.metadata?.csv_filename,
            csv_status: msg.metadata?.csv_status,
            file_ids: msg.metadata?.file_ids
          }))
        );
      }
      
      dispatch({ type: 'LOAD_CONVERSATION_SUCCESS', payload: conversation });
      console.log('✅ Conversation loaded:', id);
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to load conversation';
      console.error('❌ Failed to load conversation:', errorMessage);
      dispatch({ type: 'LOAD_CONVERSATION_FAILURE', payload: errorMessage });
    }
  }, []);

  // Create new conversation
  const createConversation = useCallback(async (data: CreateConversationRequest): Promise<Conversation> => {
    try {
      dispatch({ type: 'CREATE_CONVERSATION_START' });
      const conversation = await apiService.createConversation(data);
      dispatch({ type: 'CREATE_CONVERSATION_SUCCESS', payload: conversation });
      console.log('✅ Conversation created:', conversation.id);
      return conversation;
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to create conversation';
      console.error('❌ Failed to create conversation:', errorMessage);
      dispatch({ type: 'CREATE_CONVERSATION_FAILURE', payload: errorMessage });
      throw error;
    }
  }, []);

  // Send message
  const sendMessage = useCallback(async (content: string): Promise<void> => {
    if (!state.currentConversation) {
      throw new Error('No conversation selected');
    }

    if (!content.trim()) {
      throw new Error('Message cannot be empty');
    }

    try {
      // Send message via WebSocket for real-time processing
      webSocketService.sendChatMessage(state.currentConversation.id, content.trim());
      console.log('📤 Message sent via WebSocket');
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, [state.currentConversation]);

  // Clear error
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // Clear current conversation
  const clearCurrentConversation = useCallback((): void => {
    dispatch({ type: 'CLEAR_CURRENT_CONVERSATION' });
  }, []);

  // Update conversation
  const updateConversation = useCallback(async (id: string, title: string): Promise<void> => {
    try {
      const updatedConversation = await apiService.updateConversation(id, { title });
      dispatch({ type: 'UPDATE_CONVERSATION_SUCCESS', payload: updatedConversation });
      console.log('✅ Conversation updated:', id);
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to update conversation';
      console.error('❌ Failed to update conversation:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  // Delete conversation
  const deleteConversation = useCallback(async (id: string): Promise<void> => {
    try {
      await apiService.deleteConversation(id);
      dispatch({ type: 'DELETE_CONVERSATION_SUCCESS', payload: id });
      console.log('✅ Conversation deleted:', id);
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to delete conversation';
      console.error('❌ Failed to delete conversation:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  // Clear all conversations
  const clearAllConversations = useCallback(async (): Promise<void> => {
    try {
      const result = await apiService.clearAllConversations();
      dispatch({ type: 'CLEAR_ALL_CONVERSATIONS_SUCCESS' });
      console.log('✅ All conversations cleared:', result.deleted_count);
    } catch (error) {
      const errorMessage = (error as APIError).detail || 'Failed to clear all conversations';
      console.error('❌ Failed to clear all conversations:', errorMessage);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  }, []);

  // Apply pending attachment_references when messages are added/updated
  // Use useMemo to prevent unnecessary recalculations
  const uniqueMessages = useMemo(() => {
    const allMessages = [
      ...state.messages,
      ...(state.currentConversation?.messages || [])
    ];
    return Array.from(
      new Map(allMessages.map(m => [m.id || m.openai_message_id, m])).values()
    );
  }, [state.messages, state.currentConversation?.messages]);
  
  // Track processed message IDs to avoid reprocessing
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  
  useEffect(() => {
    // Process pending attachments and image files
    const hasPendingAttachments = pendingAttachmentsRef.current.size > 0;
    const hasPendingImageFiles = pendingImageFilesRef.current.size > 0;
    
    if (!hasPendingAttachments && !hasPendingImageFiles) {
      return;
    }
    
    if (uniqueMessages.length === 0) {
      return;
    }
    
    // Check each pending attachment_references
    const attachmentsToProcess: Array<[string, string[]]> = [];
    for (const [messageId, attachmentFilenames] of pendingAttachmentsRef.current.entries()) {
      // Skip if already processed
      if (processedMessageIdsRef.current.has(`attachment_${messageId}`)) {
        continue;
      }
      
      // Check if message now exists
      const messageExists = uniqueMessages.some(msg => 
        msg.id === messageId || msg.openai_message_id === messageId
      );
      
      if (messageExists) {
        attachmentsToProcess.push([messageId, attachmentFilenames]);
        processedMessageIdsRef.current.add(`attachment_${messageId}`);
      }
    }
    
    // Check each pending image_file
    const imagesToProcess: Array<[string, string[]]> = [];
    for (const [messageId, imageFileIds] of pendingImageFilesRef.current.entries()) {
      // Skip if already processed
      if (processedMessageIdsRef.current.has(`image_${messageId}`)) {
        continue;
      }
      
      // Check if message now exists
      const messageExists = uniqueMessages.some(msg => 
        msg.id === messageId || msg.openai_message_id === messageId
      );
      
      if (messageExists) {
        imagesToProcess.push([messageId, imageFileIds]);
        processedMessageIdsRef.current.add(`image_${messageId}`);
      }
    }
    
    // Process all attachments at once
    if (attachmentsToProcess.length > 0) {
      for (const [messageId, attachmentFilenames] of attachmentsToProcess) {
        dispatch({
          type: 'UPDATE_MESSAGE_ATTACHMENTS',
          payload: {
            messageId: messageId,
            attachmentFilenames: attachmentFilenames,
          },
        });
        // Remove from pending since it's applied
        pendingAttachmentsRef.current.delete(messageId);
      }
    }
    
    // Process all image files at once
    if (imagesToProcess.length > 0) {
      for (const [messageId, imageFileIds] of imagesToProcess) {
        dispatch({
          type: 'UPDATE_MESSAGE_IMAGE_FILES',
          payload: {
            messageId: messageId,
            imageFileIds: imageFileIds,
          },
        });
        // Remove from pending since it's applied
        pendingImageFilesRef.current.delete(messageId);
      }
    }
  }, [uniqueMessages, dispatch]);

  // Context value
  const value: ChatContextType = {
    ...state,
    loadConversations,
    loadConversation,
    createConversation,
    updateConversation,
    deleteConversation,
    clearAllConversations,
    sendMessage,
    clearError,
    clearCurrentConversation,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

// Hook to use chat context
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatProvider;
